import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { withTranslation } from 'react-i18next'
import * as UIcons from '@iconscout/react-unicons'
import { mutate } from 'swr'
import usePermissions from 'hooks/usePermissions'
import SectionCard from '../shared/SectionCard'
import QuickLinkCard from '../shared/QuickLinkCard'
import AddBranch from '../Branches/components/AddBranch'
import AddPOS from '../POSManagment/components/AddPOS'
import UserForm from '../UserManagement/components/UserForm'
import RoleWizard from '../RoleManagement/components/RoleWizard'

export default withTranslation()(function Hub({ t }) {
  const navigate = useNavigate()
  const { isPermitted } = usePermissions()
  const [showAddBranch, setShowAddBranch] = useState(false)
  const [showAddPOS, setShowAddPOS] = useState(false)
  const [showAddUser, setShowAddUser] = useState(false)
  const [showAddRole, setShowAddRole] = useState(false)

  const sections = [
    {
      url: '/settings/business-organization',
      icon: UIcons.UilBuilding,
      color: 'blue',
      title: t('settings.hub.business.title'),
      description: t('settings.hub.business.description'),
      items: [
        t('settings.hub.business.items.info'),
        t('settings.hub.business.items.contact'),
        t('settings.hub.business.items.branches'),
        t('settings.hub.business.items.pos')
      ]
    },
    {
      url: '/settings/team-access',
      icon: UIcons.UilUsersAlt,
      color: 'purple',
      title: t('settings.hub.team.title'),
      description: t('settings.hub.team.description'),
      items: [
        t('settings.hub.team.items.users'),
        t('settings.hub.team.items.roles'),
        t('settings.hub.team.items.permissions'),
        t('settings.hub.team.items.activity')
      ]
    },
    {
      url: '/settings/account-security',
      icon: UIcons.UilShieldCheck,
      color: 'green',
      title: t('settings.hub.account.title'),
      description: t('settings.hub.account.description'),
      items: [
        t('settings.hub.account.items.profile'),
        t('settings.hub.account.items.password'),
        t('settings.hub.account.items.loginPolicy'),
        t('settings.hub.account.items.twoFactor'),
        t('settings.hub.account.items.sessions')
      ]
    }
  ]

  const quickLinks = [
    {
      key: 'branch',
      icon: UIcons.UilBuilding,
      title: t('settings.hub.quick_links.branch.title'),
      description: t('settings.hub.quick_links.branch.description'),
      btnText: t('settings.hub.quick_links.branch.btn'),
      disabled: !isPermitted?.create_branches,
      onClick: () => setShowAddBranch(true)
    },
    {
      key: 'pos',
      icon: UIcons.UilCalculator,
      title: t('settings.hub.quick_links.pos.title'),
      description: t('settings.hub.quick_links.pos.description'),
      btnText: t('settings.hub.quick_links.pos.btn'),
      disabled: !isPermitted?.create_pos,
      onClick: () => setShowAddPOS(true)
    },
    {
      key: 'user',
      icon: UIcons.UilUserPlus,
      title: t('settings.hub.quick_links.user.title'),
      description: t('settings.hub.quick_links.user.description'),
      btnText: t('settings.hub.quick_links.user.btn'),
      onClick: () => setShowAddUser(true)
    },
    {
      key: 'role',
      icon: UIcons.UilShieldCheck,
      title: t('settings.hub.quick_links.role.title'),
      description: t('settings.hub.quick_links.role.description'),
      btnText: t('settings.hub.quick_links.role.btn'),
      onClick: () => setShowAddRole(true)
    },
    {
      key: 'account',
      icon: UIcons.UilLock,
      title: t('settings.hub.quick_links.account.title'),
      description: t('settings.hub.quick_links.account.description'),
      btnText: t('settings.hub.quick_links.account.btn'),
      onClick: () => navigate('/settings/account-security')
    }
  ]

  return (
    <section className="w-full">
      <h1 className="text-2xl font-bold text-dark-blue mb-5">{t('settings.hub.title')}</h1>

      <div className="grid gap-6 md:grid-cols-3 mb-6">
        {
          sections.map((section) => <SectionCard key={section.url} {...section} />)
        }
      </div>

      <div className="w-full bg-white rounded-2xl border border-border-lines shadow-sm p-4">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-9 h-9 rounded-lg bg-[#C9F0D6] flex items-center justify-center shrink-0">
            <UIcons.UilShieldCheck className="text-success" size={18} />
          </div>
          <h2 className="text-base font-semibold text-dark-blue">{t('settings.hub.quick_links.title')}</h2>
        </div>

        <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-border-lines">
          {
            quickLinks.map(({ key, ...quickLink }) => <QuickLinkCard key={key} {...quickLink} />)
          }
        </div>
      </div>

      {showAddBranch && <AddBranch handleClick={() => setShowAddBranch(false)} onSaved={() => mutate('merchants/branches')} />}
      {showAddPOS && <AddPOS handleClick={() => setShowAddPOS(false)} onSaved={() => mutate('pos')} />}
      {showAddUser && <UserForm handleClick={() => setShowAddUser(false)} onSaved={() => mutate('user-mgmt/users')} />}
      {showAddRole && <RoleWizard handleClick={() => setShowAddRole(false)} onSaved={() => mutate('user-mgmt/roles')} />}
    </section>
  )
})
